import { Bhashyam, BhashyamCreate, BhashyamUpdate, PHILOSOPHIES, LANGUAGES } from '../types/bhashyam';
import { apiClient } from './sutras';

export const getBhashyams = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  language?: string,
  philosophy?: string
): Promise<Bhashyam[]> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  // If no specific philosophy is requested, fetch all philosophies
  if (!philosophy) {
    const allBhashyams: Bhashyam[] = [];
    for (const phil of Object.values(PHILOSOPHIES)) {
      // If no specific language is requested, fetch all languages
      if (!language) {
        for (const lang of Object.values(LANGUAGES)) {
          try {
            const url = `/sutras/${projectName}/${section}/${chapter}/${number}/bhashyam?phil=${phil}&lang=${lang}`;
            console.log('Fetching bhashyams for philosophy:', phil, 'language:', lang, 'URL:', url);
            const response = await apiClient.get(url);
            if (response.data) {
              allBhashyams.push(response.data);
            }
          } catch (err) {
            // Ignore 404 errors for specific language/philosophy combinations
            if (!(err instanceof Error && err.message.includes('404'))) {
              console.error(`Error fetching bhashyams for philosophy ${phil}, language ${lang}:`, err);
            }
          }
        }
      } else {
        // If a specific language is requested, fetch only that one
        try {
          const url = `/sutras/${projectName}/${section}/${chapter}/${number}/bhashyam?phil=${phil}&lang=${language}`;
          console.log('Fetching bhashyams for philosophy:', phil, 'language:', language, 'URL:', url);
          const response = await apiClient.get(url);
          if (response.data) {
            allBhashyams.push(response.data);
          }
        } catch (err) {
          // Ignore 404 errors for specific philosophy
          if (!(err instanceof Error && err.message.includes('404'))) {
            console.error(`Error fetching bhashyams for philosophy ${phil}, language ${language}:`, err);
          }
        }
      }
    }
    return allBhashyams;
  }
  
  // If a specific philosophy is requested, fetch only that one
  // If no specific language is requested, fetch all languages
  if (!language) {
    const allBhashyams: Bhashyam[] = [];
    for (const lang of Object.values(LANGUAGES)) {
      try {
        const url = `/sutras/${projectName}/${section}/${chapter}/${number}/bhashyam?phil=${philosophy}&lang=${lang}`;
        console.log('Fetching bhashyams for philosophy:', philosophy, 'language:', lang, 'URL:', url);
        const response = await apiClient.get(url);
        if (response.data) {
          allBhashyams.push(response.data);
        }
      } catch (err) {
        // Ignore 404 errors for specific language
        if (!(err instanceof Error && err.message.includes('404'))) {
          console.error(`Error fetching bhashyams for philosophy ${philosophy}, language ${lang}:`, err);
        }
      }
    }
    return allBhashyams;
  }
  
  // If both philosophy and language are specified, fetch only that combination
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/bhashyam?phil=${philosophy}&lang=${language}`;
  console.log('GET Bhashyams URL:', url);
  const response = await apiClient.get(url);
  
  // Handle both single object and array responses
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  } else if (data && typeof data === 'object') {
    return [data];
  } else {
    console.warn('Unexpected response format:', data);
    return [];
  }
};

export const createBhashyam = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  bhashyam: BhashyamCreate
): Promise<Bhashyam> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  const requestBody = { ...bhashyam, section };
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/bhashyam`;
  console.log('CREATE Bhashyam URL:', url);
  console.log('CREATE Bhashyam Data:', requestBody);
  const response = await apiClient.post(url, requestBody);
  return response.data;
};

export const updateBhashyam = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  bhashyam: BhashyamUpdate
): Promise<Bhashyam> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  const requestBody = { ...bhashyam, section };
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/bhashyam?lang=${bhashyam.language}&phil=${bhashyam.philosophy}`;
  console.log('UPDATE Bhashyam URL:', url);
  console.log('UPDATE Bhashyam Data:', requestBody);
  const response = await apiClient.put(url, requestBody);
  return response.data;
};

export const deleteBhashyam = async (
  projectName: string,
  section: string,
  chapter: number,
  number: number,
  language: string,
  philosophy: string
): Promise<void> => {
  if (!projectName) {
    throw new Error('Project name is required');
  }
  
  const url = `/sutras/${projectName}/${section}/${chapter}/${number}/bhashyam?lang=${language}&phil=${philosophy}`;
  console.log('DELETE Bhashyam URL:', url);
  await apiClient.delete(url);
}; 